import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Sparkles,
    Send,
    Plus,
    ThumbsUp,
    ThumbsDown,
    Loader2,
    ExternalLink,
} from "lucide-react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { publicAssetUrl, errorMessage } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import {
    submitAIQuery,
    sendAIFeedback,
    getStoredSessionId,
    setStoredSessionId,
    clearStoredSession,
} from "@/lib/customerAI";
import { getLocalLocation } from "@/lib/location";
import { VoiceCaptureButton } from "@/components/customer/VoiceCaptureButton";

const SUGGESTED_PROMPTS = [
    "Dinner for 4 under ₹1,500",
    "Birthday gift under ₹5,000",
    "Weekend sale on electronics",
    "Beauty deals near me",
];

/** Format INR without decimals, add thousand separators. */
const inr = (n) => (typeof n === "number" ? `₹${Math.round(n).toLocaleString("en-IN")}` : null);

/**
 * Floating AI assistant — orange sparkle FAB in the bottom-right that opens
 * a shadcn Sheet on the right (desktop) or bottom (mobile). Anonymous users
 * see a login-prompt bubble the first time they tap the FAB.
 *
 * Keep this component self-contained: no external state, no route changes.
 */
export function AIAssistant() {
    const { user, status } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    // messages: [{role: "user"|"assistant", content, cards?, turn_id?, feedback?: {idx: "up"|"down"}}]
    const [messages, setMessages] = useState([]);
    const [sessionId, setSessionId] = useState(() => getStoredSessionId());
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    const authed = status === "authenticated" && !!user;
    const isCustomer = authed && user?.role === "CUSTOMER";

    const onFabClick = () => {
        trackEvent("home_view", { source: "ai_fab_click" });
        if (!authed) {
            toast("Sign in to chat with Offernest AI", {
                action: {
                    label: "Log in",
                    onClick: () => navigate("/customer/login"),
                },
            });
            return;
        }
        if (!isCustomer) {
            toast.error("Offernest AI is available for customer accounts only.");
            return;
        }
        setOpen(true);
    };

    // Auto-scroll to the latest message when the log updates.
    useEffect(() => {
        if (!open || !scrollRef.current) return;
        requestAnimationFrame(() => {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        });
    }, [messages, open]);

    // Focus the input when opening.
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => inputRef.current?.focus(), 200);
        return () => clearTimeout(t);
    }, [open]);

    const sendQuery = useCallback(async (rawQuery) => {
        const query = (rawQuery ?? input).trim();
        if (!query || busy) return;
        setBusy(true);

        // Append the user message optimistically.
        const nextMessages = [...messages, { role: "user", content: query }];
        setMessages(nextMessages);
        setInput("");

        trackEvent("search_submit", { query, source: "ai_assistant" });

        // Include the last 6 turns for context.
        const previous_turns = nextMessages
            .slice(-8)
            .map((m) => ({ role: m.role, content: m.content }));

        const loc = getLocalLocation();
        const location = loc?.lat && loc?.lng ? { lat: loc.lat, lng: loc.lng } : undefined;

        try {
            const res = await submitAIQuery({
                query,
                session_id: sessionId || undefined,
                location,
                previous_turns,
            });
            const newSid = res.meta?.session_id;
            if (newSid && newSid !== sessionId) {
                setSessionId(newSid);
                setStoredSessionId(newSid);
            }
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content: res.reply,
                    cards: res.cards || [],
                    turn_id: res.turn_id,
                    feedback: {},
                },
            ]);
        } catch (e) {
            const isRateLimit = e?.response?.status === 429;
            const msg = isRateLimit
                ? (e?.response?.data?.detail || "You're chatting fast — take a short break!")
                : errorMessage(e);
            setMessages((prev) => [
                ...prev,
                { role: "assistant", content: msg, cards: [], isError: true },
            ]);
        } finally {
            setBusy(false);
        }
    }, [busy, input, messages, sessionId]);

    const onSuggestedChip = (text) => {
        setInput(text);
        // Small delay so the input paints before submit.
        setTimeout(() => sendQuery(text), 40);
    };

    const onNewChat = () => {
        clearStoredSession();
        setSessionId(null);
        setMessages([]);
        setInput("");
        inputRef.current?.focus();
    };

    const onCardTap = (card) => {
        trackEvent("post_view", {
            post_id: card.post_id,
            source: "ai_assistant",
        });
        setOpen(false);
        navigate(`/posts/${card.post_id}`);
    };

    const onFeedback = async (msgIdx, cardIdx, turn_id, helpful) => {
        // Update UI immediately.
        setMessages((prev) =>
            prev.map((m, i) => {
                if (i !== msgIdx) return m;
                return {
                    ...m,
                    feedback: { ...(m.feedback || {}), [cardIdx]: helpful ? "up" : "down" },
                };
            }),
        );
        await sendAIFeedback({ turn_id, card_index: cardIdx, helpful });
    };

    const showSuggested = messages.length === 0;

    return (
        <>
            {/* Floating action button */}
            <button
                onClick={onFabClick}
                aria-label="Ask Offernest AI"
                data-testid="home-ai-fab"
                className="group fixed bottom-24 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--brand-orange)] text-white shadow-lg shadow-orange-500/30 transition hover:scale-105 hover:shadow-xl active:scale-95 md:bottom-6 md:right-6"
                style={{ animation: "ai-fab-pulse 2.4s ease-in-out 2" }}
            >
                <Sparkles size={26} className="drop-shadow" />
            </button>

            {/* Inline keyframes so this file is drop-in without touching global CSS. */}
            <style>{`
                @keyframes ai-fab-pulse {
                    0%, 100% { box-shadow: 0 10px 25px -5px rgba(255,107,0,0.35); transform: scale(1); }
                    50%      { box-shadow: 0 14px 34px 0 rgba(255,107,0,0.55); transform: scale(1.06); }
                }
            `}</style>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    side="right"
                    className="flex w-full flex-col p-0 sm:max-w-md"
                    data-testid="ai-drawer"
                >
                    <SheetHeader className="border-b border-slate-100 bg-white px-6 py-4 text-left">
                        <SheetTitle className="flex items-center gap-2 font-heading text-xl font-extrabold text-[var(--text-primary)]">
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF3E6] text-[var(--brand-orange)]">
                                <Sparkles size={16} />
                            </span>
                            Ask Offernest AI
                        </SheetTitle>
                        <SheetDescription className="font-body text-xs text-[var(--text-muted)]">
                            Your personal shopping assistant. Try: <span className="italic">"Birthday gift under ₹5,000"</span>
                        </SheetDescription>
                    </SheetHeader>

                    {/* Chat log */}
                    <div
                        ref={scrollRef}
                        className="flex-1 overflow-y-auto bg-slate-50 px-4 py-4 space-y-4"
                        data-testid="ai-chat-log"
                    >
                        {showSuggested && (
                            <AssistantBubble>
                                <p className="font-body text-sm text-[var(--text-primary)]">
                                    Hi! I can help you discover offers in your area. Ask me anything about
                                    shopping, dining, or gifts.
                                </p>
                            </AssistantBubble>
                        )}

                        {messages.map((m, i) =>
                            m.role === "user" ? (
                                <UserBubble key={i} idx={i}>{m.content}</UserBubble>
                            ) : (
                                <AssistantBubble key={i} idx={i} isError={m.isError}>
                                    <p className="whitespace-pre-wrap font-body text-sm text-[var(--text-primary)]">
                                        {m.content}
                                    </p>
                                    {m.cards?.length > 0 && (
                                        <CardRail
                                            cards={m.cards}
                                            msgIdx={i}
                                            turn_id={m.turn_id}
                                            feedback={m.feedback || {}}
                                            onCardTap={onCardTap}
                                            onFeedback={onFeedback}
                                        />
                                    )}
                                </AssistantBubble>
                            ),
                        )}

                        {busy && (
                            <AssistantBubble>
                                <div className="flex items-center gap-2 font-body text-sm text-[var(--text-muted)]">
                                    <Loader2 size={14} className="animate-spin text-[var(--brand-orange)]" />
                                    Thinking through your request…
                                </div>
                            </AssistantBubble>
                        )}
                    </div>

                    {/* Suggested prompt chips — only on first open */}
                    {showSuggested && (
                        <div
                            className="border-t border-slate-100 bg-white px-4 py-3"
                            data-testid="ai-suggested-chips"
                        >
                            <div className="mb-2 font-heading text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                                Try one of these
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {SUGGESTED_PROMPTS.map((p, idx) => (
                                    <button
                                        key={p}
                                        onClick={() => onSuggestedChip(p)}
                                        data-testid={`ai-suggested-chip-${idx}`}
                                        className="rounded-full border border-slate-200 bg-white px-3 py-1.5 font-heading text-xs font-semibold text-[var(--text-primary)] hover:border-[var(--brand-orange)] hover:bg-[#FFF3E6]"
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Input row */}
                    <div className="border-t border-slate-100 bg-white p-4">
                        <div className="flex items-center gap-2">
                            <VoiceCaptureButton
                                surface="ai_drawer"
                                testId="ai-mic-btn"
                                onConfirm={(text) => sendQuery(text)}
                                onEdit={(text) => { setInput(text); setTimeout(() => inputRef.current?.focus(), 40); }}
                            />
                            <input
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        sendQuery();
                                    }
                                }}
                                placeholder="Ask about deals, gifts, dinners…"
                                disabled={busy}
                                data-testid="ai-input"
                                className="flex-1 rounded-full border border-slate-200 bg-white px-4 py-2.5 font-body text-sm text-[var(--text-primary)] focus:border-[var(--brand-orange)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)] disabled:opacity-50"
                            />
                            <button
                                onClick={() => sendQuery()}
                                disabled={!input.trim() || busy}
                                aria-label="Send"
                                data-testid="ai-send-btn"
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--brand-orange)] text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                            </button>
                        </div>
                        {messages.length > 0 && (
                            <button
                                onClick={onNewChat}
                                data-testid="ai-new-chat-btn"
                                className="mt-2 inline-flex items-center gap-1 font-heading text-xs font-semibold text-[var(--text-muted)] hover:text-[var(--brand-orange)]"
                            >
                                <Plus size={12} /> New chat
                            </button>
                        )}
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}

/* --- bubbles + cards ------------------------------------------------------- */

function UserBubble({ children, idx }) {
    return (
        <div className="flex justify-end" data-testid={`ai-message-${idx}`}>
            <div className="max-w-[80%] rounded-2xl rounded-tr-md bg-[var(--brand-orange)] px-4 py-2 font-body text-sm text-white shadow-sm">
                {children}
            </div>
        </div>
    );
}

function AssistantBubble({ children, idx, isError }) {
    return (
        <div
            className="flex justify-start"
            data-testid={typeof idx === "number" ? `ai-message-${idx}` : undefined}
        >
            <div className="max-w-[92%] space-y-2">
                <div className="flex items-start gap-2">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FFF3E6] text-[var(--brand-orange)]">
                        <Sparkles size={12} />
                    </div>
                    <div
                        className={
                            "rounded-2xl rounded-tl-md px-4 py-2.5 shadow-sm " +
                            (isError
                                ? "bg-rose-50 ring-1 ring-rose-100"
                                : "bg-white ring-1 ring-slate-100")
                        }
                    >
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}

function CardRail({ cards, msgIdx, turn_id, feedback, onCardTap, onFeedback }) {
    return (
        <div className="mt-2 -mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-2">
            {cards.map((c, cardIdx) => {
                const currentFb = feedback[cardIdx];
                return (
                    <div
                        key={`${c.post_id}-${cardIdx}`}
                        data-testid={`ai-card-${cardIdx}-${c.post_id}`}
                        className="card-soft w-56 shrink-0 snap-start !p-3"
                        style={{ borderRadius: 18 }}
                    >
                        <button
                            onClick={() => onCardTap(c)}
                            className="block w-full text-left"
                            aria-label={`View ${c.title}`}
                        >
                            <div className="aspect-[4/3] w-full overflow-hidden rounded-lg bg-slate-100">
                                {c.hero_image_url ? (
                                    <img
                                        src={publicAssetUrl(c.hero_image_url)}
                                        alt={c.title}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center bg-[#FFF3E6] font-heading text-2xl font-extrabold text-[var(--brand-orange)]">
                                        {(c.shop_name || "?").charAt(0).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <div className="mt-2">
                                {c.shop_name && (
                                    <div className="truncate font-heading text-[11px] font-semibold uppercase tracking-wider text-[var(--brand-orange)]">
                                        {c.shop_name}
                                    </div>
                                )}
                                <div className="line-clamp-2 font-heading text-sm font-semibold text-[var(--text-primary)]">
                                    {c.title}
                                </div>
                                <div className="mt-1 flex items-baseline gap-2">
                                    {inr(c.price_after) && (
                                        <span className="font-heading text-base font-extrabold text-[var(--brand-orange)]">
                                            {inr(c.price_after)}
                                        </span>
                                    )}
                                    {inr(c.price_before) && c.price_before > (c.price_after || 0) && (
                                        <span className="font-body text-[11px] text-[var(--text-muted)] line-through">
                                            {inr(c.price_before)}
                                        </span>
                                    )}
                                    {typeof c.discount_percent === "number" && (
                                        <span className="ml-auto rounded-full bg-emerald-50 px-2 py-0.5 font-heading text-[10px] font-bold text-emerald-700">
                                            -{c.discount_percent}%
                                        </span>
                                    )}
                                </div>
                                {c.reason && (
                                    <div className="mt-1 line-clamp-2 font-body text-[10px] text-[var(--text-muted)]">
                                        {c.reason}
                                    </div>
                                )}
                            </div>
                        </button>
                        <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => onFeedback(msgIdx, cardIdx, turn_id, true)}
                                    aria-label="Helpful"
                                    data-testid={`ai-thumbs-up-${cardIdx}`}
                                    className={
                                        "flex h-6 w-6 items-center justify-center rounded-full transition " +
                                        (currentFb === "up"
                                            ? "bg-emerald-50 text-emerald-700"
                                            : "text-[var(--text-muted)] hover:bg-slate-100")
                                    }
                                >
                                    <ThumbsUp size={12} />
                                </button>
                                <button
                                    onClick={() => onFeedback(msgIdx, cardIdx, turn_id, false)}
                                    aria-label="Not helpful"
                                    data-testid={`ai-thumbs-down-${cardIdx}`}
                                    className={
                                        "flex h-6 w-6 items-center justify-center rounded-full transition " +
                                        (currentFb === "down"
                                            ? "bg-rose-50 text-rose-700"
                                            : "text-[var(--text-muted)] hover:bg-slate-100")
                                    }
                                >
                                    <ThumbsDown size={12} />
                                </button>
                            </div>
                            <button
                                onClick={() => onCardTap(c)}
                                className="inline-flex items-center gap-1 font-heading text-[11px] font-semibold text-[var(--brand-orange)] hover:underline"
                            >
                                View <ExternalLink size={10} />
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default AIAssistant;
